import React, { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { pdf } from '@react-pdf/renderer';
import makeFileDownload from 'helpers/makeFileDownload';

import { getDisplayName } from 'lane-shared/helpers';

import type { ClientTask, TaskAuditLog } from 'graphql-query-contracts';

import PMTaskDetailsPDF from '../components/PMTaskDetailsPDF';
import { convertTask } from './convertTask';

type UseExportPmTaskPdfParams = {
  task: ClientTask;
  refetch: () => Promise<unknown>;
  refetchAuditLogs: () => Promise<unknown>;
  createdUser: Parameters<typeof getDisplayName>[0];
  auditLogs: TaskAuditLog[];
};

export function useExportPmTaskPdf({
  task,
  refetch,
  refetchAuditLogs,
  createdUser,
  auditLogs,
}: UseExportPmTaskPdfParams) {
  const { t } = useTranslation();
  const taskRef = useRef(task);

  useEffect(() => {
    taskRef.current = task;
  }, [task]);

  const handleExport = async () => {
    try {
      await Promise.all([refetch(), refetchAuditLogs()]);

      const blob = await pdf(
        <PMTaskDetailsPDF
          taskData={convertTask(taskRef.current)}
          createdUserName={getDisplayName(createdUser) || ''}
          auditLogs={auditLogs}
          t={t}
        />
      ).toBlob();

      makeFileDownload({
        blob,
        name: `${task?.userFriendlyID}.pdf`,
        type: 'application/pdf',
      });
    } catch (error) {
      await window.Alert.alert({
        title: t('An Error Occurred!'),
        message: t('Unable to download the task. Please try again.'),
      });
      console.error('Error while downloading the PDF:', error);
    }
  };

  return { handleExport };
}
